import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

type SuiteTrackingLayoutProps = {
  testId?: string;
  municipalitiesLoading: boolean;
  municipalitiesCount: number;
  municipalitySelected: boolean;
  emptyIcon: LucideIcon;
  selectMunicipalityMessage: string;
  summaryStrip: ReactNode;
  sidebar: ReactNode;
  /** Overrides the stacked (below lg) sidebar height cap for panels whose sidebar carries filters. */
  sidebarClassName?: string;
  detail: ReactNode;
  footer?: ReactNode;
};

export function SuiteTrackingLayout({
  testId,
  municipalitiesLoading,
  municipalitiesCount,
  municipalitySelected,
  emptyIcon: EmptyIcon,
  selectMunicipalityMessage,
  summaryStrip,
  sidebar,
  sidebarClassName,
  detail,
  footer,
}: SuiteTrackingLayoutProps) {
  if (municipalitiesLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading municipalities…
      </div>
    );
  }

  if (municipalitiesCount === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-muted-foreground">
        No municipalities are available for your account.
      </div>
    );
  }

  if (!municipalitySelected) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
        <EmptyIcon className="h-8 w-8 opacity-40" />
        <p className="max-w-sm text-sm">{selectMunicipalityMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid={testId}>
      {summaryStrip}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <aside
          className={cn(
            "flex max-h-[40%] min-h-0 w-full flex-col border-b bg-muted/10 lg:max-h-none lg:w-[22rem] lg:shrink-0 lg:border-b-0 lg:border-r",
            sidebarClassName,
          )}
        >
          {sidebar}
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{detail}</div>
      </div>
      {footer}
    </div>
  );
}
